import { FC, useState } from "react";
import FTModal from "../../components/common/FTModal";
import AppSearchBox from "../../components/searchBox";
import { ClusterVehiclesListItem } from "../../utils/map";
import { useLoggedInUserData } from "../../utils/user";

type ModalClusterVehiclesListProps = {
  visible: boolean;
  count: number;
  entityLabel: string;
  markers: ClusterVehiclesListItem[];
  showViewDetails?: boolean;
  showCameraId?: boolean;
  onHide: () => void;
  onVehicleClick?: (deviceId: string) => void;
};

const getClusterMarkerCameraId = (marker: ClusterVehiclesListItem) => {
  return marker.unit_name || marker.cid || "-";
};

const ModalClusterVehiclesList: FC<ModalClusterVehiclesListProps> = ({
  visible,
  count,
  entityLabel,
  markers,
  showViewDetails = true,
  showCameraId = false,
  onHide,
  onVehicleClick,
}) => {
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const [searchQuery, setSearchQuery] = useState("");

  const filteredMarkers = searchQuery.trim()
    ? markers.filter((marker) => {
        const q = searchQuery.trim().toLowerCase();
        const deviceId = (
          marker.deviceid ||
          marker.device_id ||
          ""
        ).toLowerCase();
        return (
          deviceId.includes(q) ||
          (showCameraId
            ? getClusterMarkerCameraId(marker).toLowerCase().includes(q)
            : false) ||
          (marker.description || marker.ft_device_description || "")
            .toLowerCase()
            .includes(q) ||
          (marker.displayname || marker.ft_displayname || "")
            .toLowerCase()
            .includes(q) ||
          (marker.drivername || "").toLowerCase().includes(q) ||
          (marker.serial_number || "").toLowerCase().includes(q)
        );
      })
    : markers;

  const searchNode = (
    <AppSearchBox
      controlledValue={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      onClear={() => setSearchQuery("")}
      placeholder="Search assets..."
      wrapperClassName="mr-3 w-56"
      inputClassName="w-full rounded-md border border-heading-gray888/30 py-2 ps-10 pe-8 text-sm shadow-none focus:outline-none focus:ring-1 focus:ring-accent-blue-dark"
    />
  );

  return (
    <FTModal
      visible={visible}
      onHide={onHide}
      heading={
        <>
          {`${count} ${entityLabel}`}
          {!!searchQuery.trim() && (
            <span className="text-base font-bold text-heading-gray888/75 ml-2">
              ({filteredMarkers.length} match
              {filteredMarkers.length !== 1 ? "es" : ""})
            </span>
          )}
        </>
      }
      containerStyle={`${showDriver ? "w-[1020px]" : "w-[860px]"} ${showCameraId ? "xl:w-[1160px]" : ""} max-w-[calc(100vw-2rem)]`}
      position="center"
      nodeLeftOfCloseButton={searchNode}
    >
      <div className="h-[70vh] overflow-y-auto border border-heading-gray888/20 rounded-lg">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-accent-blue-pale z-[1]">
            <tr>
              {showCameraId && (
                <th className="px-4 py-3 font-bold text-accent-blue-dark">
                  Camera ID
                </th>
              )}
              <th className="px-4 py-3 font-bold text-accent-blue-dark">
                Asset ID
              </th>
              <th className="px-4 py-3 font-bold text-accent-blue-dark">
                Description
              </th>
              <th className="px-4 py-3 font-bold text-accent-blue-dark">
                Display Name
              </th>
              {showDriver && (
                <th className="px-4 py-3 font-bold text-accent-blue-dark">
                  Driver Name
                </th>
              )}
              {showViewDetails && (
                <th className="px-4 py-3 font-bold text-accent-blue-dark">
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredMarkers.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    (showDriver ? 4 : 3) +
                    (showCameraId ? 1 : 0) +
                    (showViewDetails ? 1 : 0)
                  }
                  className="px-3 py-6 text-center text-heading-gray888"
                >
                  No assets match your search.
                </td>
              </tr>
            ) : (
              filteredMarkers.map((marker, index) => {
                const deviceId = marker.deviceid || marker.device_id || "";
                const description =
                  marker.description || marker.ft_device_description || "-";
                const displayName =
                  marker.displayname || marker.ft_displayname || "-";
                const cameraId = getClusterMarkerCameraId(marker);

                return (
                  <tr
                    key={`${deviceId || "unknown"}-${index}`}
                    className="border-t border-heading-gray888/20 hover:bg-gray-50"
                  >
                    {showCameraId && (
                      <td className="px-4 py-3 text-heading-gray888">
                        {cameraId}
                      </td>
                    )}
                    <td className="px-4 py-3 text-heading-black">
                      {deviceId || "-"}
                    </td>
                    <td className="px-4 py-3 text-heading-gray888">
                      {description}
                    </td>
                    <td className="px-4 py-3 text-heading-gray888">
                      {displayName}
                    </td>
                    {showDriver && (
                      <td className="px-4 py-3 text-heading-gray888">
                        {marker.drivername || "-"}
                      </td>
                    )}
                    {showViewDetails && (
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          className="text-sm font-semibold px-3 py-2 rounded-md border border-accent-blue-dark text-accent-blue-dark hover:bg-accent-blue-dark hover:text-white disabled:opacity-50"
                          onClick={() => deviceId && onVehicleClick?.(deviceId)}
                          disabled={!deviceId || !onVehicleClick}
                        >
                          View details
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </FTModal>
  );
};

export default ModalClusterVehiclesList;
